import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Check, X, Loader2 } from 'lucide-react';
import { authService } from '@services/authService';
import { referralService } from '@services/referralService';
import toast from 'react-hot-toast';
import { useAuthStore } from '@store/authStore';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import { SEOHead, SEO_META_TAGS, useSEO } from '@/seo';

// Components
import LoginHeader from './components/LoginHeader';
import GoogleLoginButton from './components/GoogleLoginButton';
import EmailLoginForm from './components/EmailLoginForm';
import PhoneLoginForm from './components/PhoneLoginForm';
import LoginFooter from './components/LoginFooter';

const Login = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [isPhoneFormOpen, setIsPhoneFormOpen] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [referrerName, setReferrerName] = useState('');
  const [refereeReward, setRefereeReward] = useState(0);
  const [showReferralInput, setShowReferralInput] = useState(false);
  const navigate = useNavigate();
  const { user, login, setLoading } = useAuthStore();
  const hasProcessedToken = useRef(false);
  const validateTimeout = useRef<NodeJS.Timeout | null>(null);

  const validateReferralCode = useCallback(async (code: string) => {
    if (!code || code.length < 4) {
      setReferralStatus('idle');
      setReferrerName('');
      sessionStorage.removeItem('referral_code');
      return;
    }
    setReferralStatus('checking');
    try {
      const result = await referralService.validateCode(code);
      if (result.valid) {
        setReferralStatus('valid');
        setReferrerName(result.referrer_name || '');
        setRefereeReward(result.referee_reward || 0);
        sessionStorage.setItem('referral_code', code);
      } else {
        setReferralStatus('invalid');
        setReferrerName('');
        sessionStorage.removeItem('referral_code');
      }
    } catch {
      setReferralStatus('invalid');
      setReferrerName('');
      sessionStorage.removeItem('referral_code');
    }
  }, []);

  // Capture referral code from URL on mount, or restore from sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      const cleaned = refCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setReferralCode(cleaned);
      setShowReferralInput(true);
      validateReferralCode(cleaned);
    } else {
      const stored = sessionStorage.getItem('referral_code');
      if (stored) {
        setReferralCode(stored);
        setShowReferralInput(true);
        validateReferralCode(stored);
      }
    }
  }, [validateReferralCode]);

  const handleReferralCodeChange = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    setReferralCode(cleaned);

    if (validateTimeout.current) clearTimeout(validateTimeout.current);

    if (!cleaned) {
      setReferralStatus('idle');
      setReferrerName('');
      sessionStorage.removeItem('referral_code');
      return;
    }

    validateTimeout.current = setTimeout(() => validateReferralCode(cleaned), 600);
  };

  // Handle token from URL (Google OAuth redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token && !hasProcessedToken.current) {
      hasProcessedToken.current = true;

      // Clean URL immediately to prevent re-processing
      window.history.replaceState({}, '', window.location.pathname);

      const handleTokenLogin = async () => {
        try {
          setLoading(true);
          useAuthStore.setState({ token: token });

          const authData = await authService.checkAuth();

          if (authData.authenticated && authData.user) {
            login(authData.user, token);

            // Apply referral code if present (for Google OAuth signup)
            const referralCode = sessionStorage.getItem('referral_code');
            if (referralCode) {
              let shouldClearReferralCode = false;
              try {
                const result = await referralService.applyReferral(referralCode);
                if (result.success) {
                  shouldClearReferralCode = true;
                  toast.success(result.message);
                  if (result.wallet_balance !== undefined) {
                    useAuthStore.getState().updateBalance(result.wallet_balance);
                  }
                }
              } catch (referralError: any) {
                // Preserve referral code for retry on transient failures (network/5xx).
                if (referralError?.response?.status === 400) {
                  shouldClearReferralCode = true;
                }
              }
              if (shouldClearReferralCode) {
                sessionStorage.removeItem('referral_code');
              }
            }

            toast.success('Successfully logged in with Google!');
            navigate('/dashboard', { replace: true });
          } else {
            useAuthStore.setState({ token: null });
            toast.error('Failed to verify authentication');
            hasProcessedToken.current = false;
          }
        } catch (error) {
          console.error('Error during token login:', error);
          useAuthStore.setState({ token: null });
          toast.error('Authentication check failed');
          hasProcessedToken.current = false;
        } finally {
          setLoading(false);
        }
      };

      handleTokenLogin();
    }
  }, [navigate, login, setLoading]);

  // Redirect to dashboard if already logged in (skip if processing Google token)
  useEffect(() => {
    if (user && !hasProcessedToken.current) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      console.log('Initiating Google login...');
      const response = await authService.getGoogleAuthUrl();
      console.log('Google auth response:', response);

      if (response.authorization_url) {
        console.log('Redirecting to:', response.authorization_url);
        // Redirect to Google OAuth
        window.location.href = response.authorization_url;
      } else {
        console.error('No authorization_url in response');
        toast.error('Failed to initiate Google login');
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      toast.error(error?.response?.data?.error || 'Failed to login with Google');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-gray-800 flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-cosmic-200 via-cosmic-300 to-cosmic-200">
      <SEOHead {...useSEO(SEO_META_TAGS.login.English, 'English')} noindex={true} />

      {/* Cosmic Background Layer */}
      <BackgroundLayer />

      <div
        className="w-full max-w-[420px] relative z-10"
      >
        {/* Main Login Card */}
        <div className="bg-cosmic-900/60 backdrop-blur-2xl border border-white/20 rounded-[40px] p-6 md:p-10 shadow-2xl relative overflow-hidden group">

          {/* Internal Glow Effects */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-[2px] bg-gradient-to-r from-transparent via-primary-500/40 to-transparent" />
          <div className="absolute -top-32 -right-32 w-64 h-64 bg-secondary-500/10 blur-[80px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-primary-500/10 blur-[80px] rounded-full pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center w-full">

            <LoginHeader />

            {/* Email Login Form */}
            {!isPhoneFormOpen && (
              <EmailLoginForm
                isOpen={isEmailFormOpen}
                onOpenChange={setIsEmailFormOpen}
              />
            )}

            {/* Phone Login Form */}
            {!isEmailFormOpen && (
              <PhoneLoginForm
                isOpen={isPhoneFormOpen}
                onOpenChange={setIsPhoneFormOpen}
              />
            )}

            {/* Divider */}
            {!isEmailFormOpen && !isPhoneFormOpen && (
              <div className="flex items-center w-full gap-4 my-4">
                <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-white/20" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans">or</span>
                <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-white/20" />
              </div>
            )}

            {/* Google Login Button */}
            {!isEmailFormOpen && !isPhoneFormOpen && (
              <GoogleLoginButton
                isLoading={isGoogleLoading}
                onClick={handleGoogleLogin}
              />
            )}

            {/* Referral Code */}
            {!isEmailFormOpen && !isPhoneFormOpen && (
              <div className="w-full mt-3">
                {!showReferralInput ? (
                  <button
                    onClick={() => setShowReferralInput(true)}
                    className="w-full text-center text-[10px] text-primary-300/50 hover:text-primary-300/80 transition-colors font-sans tracking-wide py-1"
                  >
                    Have a referral code?
                  </button>
                ) : (
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                      Referral Code
                    </label>
                    <div className="relative">
                      <Gift size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary-400/50" />
                      <input
                        type="text"
                        value={referralCode}
                        onChange={(e) => handleReferralCodeChange(e.target.value)}
                        placeholder="Enter code..."
                        className={`w-full pl-9 pr-9 py-2.5 bg-cosmic-950/50 border rounded-xl text-white text-xs font-mono tracking-widest placeholder:text-white/20 placeholder:tracking-normal focus:outline-none transition-colors ${
                          referralStatus === 'valid'
                            ? 'border-green-500/40 focus:ring-1 focus:ring-green-500/30'
                            : referralStatus === 'invalid'
                            ? 'border-red-500/40 focus:ring-1 focus:ring-red-500/30'
                            : 'border-white/15 focus:ring-1 focus:ring-primary-500/40 focus:border-primary-500/40'
                        }`}
                        autoFocus
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {referralStatus === 'checking' && (
                          <Loader2 size={14} className="text-white/40 animate-spin" />
                        )}
                        {referralStatus === 'valid' && (
                          <Check size={14} className="text-green-400" />
                        )}
                        {referralStatus === 'invalid' && (
                          <button
                            type="button"
                            onClick={() => handleReferralCodeChange('')}
                            className="text-red-400 hover:text-red-300 transition-colors p-0.5"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    {referralStatus === 'valid' && referrerName && (
                      <p className="text-[10px] text-green-400/80 ml-1 font-sans">
                        Referred by {referrerName} — you'll get ₹{refereeReward} on signup!
                      </p>
                    )}
                    {referralStatus === 'invalid' && referralCode.length >= 4 && (
                      <p className="text-[10px] text-red-400/80 ml-1 font-sans">
                        Invalid referral code
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <LoginFooter />

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
